// ---------------------------------------------------------------------------
// C4: Proactive Suggestion Display
// ---------------------------------------------------------------------------
// One-line suggestion in AI bar area with [Yes →] [Not now].
// Non-blocking. Appears when sparkle pulses.
// ---------------------------------------------------------------------------
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProactiveSuggestionData {
  id: string;
  message: string;
  action?: { label: string; href?: string };
}

interface ProactiveSuggestionProps {
  suggestion: ProactiveSuggestionData;
  onAccept: () => void;
  onDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProactiveSuggestion({ suggestion, onAccept, onDismiss }: ProactiveSuggestionProps) {
  const navigate = useNavigate();

  const handleAccept = () => {
    if (suggestion.action?.href) {
      navigate(suggestion.action.href);
    }
    onAccept();
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 animate-in slide-in-from-bottom-1 duration-200">
      <Sparkles className="h-4 w-4 flex-shrink-0 text-[var(--color-ai)]" />
      <p className="flex-1 text-sm text-foreground">{suggestion.message}</p>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleAccept}
          className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors"
        >
          {suggestion.action?.label ?? "Yes"}
          <ArrowRight className="h-3 w-3" />
        </button>
        <button
          onClick={onDismiss}
          className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
